import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { ModelDocument, ModelDocumentCandidate, ModelDocumentId, SupersededVia } from '@rock/types';
import {
    countPagesInRanges,
    type DocumentLinkFit,
    documentLinkFit,
    documentStoragePath,
    documentTitle,
    isDirectLink,
    isHumanMark,
    canSupersede,
    isSupersededEntry,
    libraryDocuments,
    type ModelDocumentEntry,
} from '@rock/manuals';
import { equipmentTypeFamiliesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { MoreHorizontal, Pencil, Replace, RotateCcw, Scissors, Trash2, Undo2 } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { CoverageContext } from './CandidatesCard';
import type { DocumentEdit } from './documentEdits';
import { EditDocumentDialog } from './EditDocumentDialog';
import {
    type ApplyTrim,
    CoveragePills,
    DocumentScopePills,
    DocumentSourceLinks,
    DocumentStateBadges,
    LanguageAnalysisBadge,
    LinkFitBadge,
    LinkKindBadge,
    PdfPreview,
    type RestoreTrim,
    TagChips,
    TrimPdfDialog,
    canAnalyzeLanguages,
} from './documentShared';

export interface DocumentsCardProps {
    /** The model's RESOLVED document entries (links joined to the shared artifacts). */
    entries: ModelDocumentEntry[];
    /** The model's candidates — a published doc's backing candidate carries its `languageAnalysis`. */
    candidates: ModelDocumentCandidate[];
    saving: boolean;
    /**
     * Save a document's publication — tags/title, brands/types and its coverage rule, all on the SHARED
     * artifact — as one edit: "what is this document, what is it for, and who does it reach" is one decision.
     */
    onEditPublication: (doc: ModelDocument, edit: DocumentEdit) => Promise<boolean>;
    /** Unpublish; `alsoNotRelevant` additionally vetoes the backing candidate so the worker won't re-propose it. */
    onUnpublish: (doc: ModelDocument, alsoNotRelevant: boolean) => Promise<boolean>;
    /** Mark `doc` superseded by another confirmed document (`null` clears the mark). */
    onSupersede: (doc: ModelDocument, supersededBy: ModelDocumentId | null) => Promise<boolean>;
    /** The model's brand/space/type — orders the edit form's type picker and tags each link's fit. */
    coverageContext: CoverageContext;
    /** Run per-page language analysis on the doc's staged candidate bytes (server-side, ~seconds). */
    onAnalyzeLanguages: (docId: ModelDocumentId) => Promise<boolean>;
    /** Cut pages out of a document's PDF, in place. */
    onApplyTrim: ApplyTrim;
    /** Restore the document's full PDF, discarding every cut made on it. */
    onRestoreTrim: RestoreTrim;
}

/**
 * The CONFIRMED documents — the model's whole library. Every entry is human-vouched by construction;
 * whether the app serves one follows from its tags (app-visible vs library-only, badged per row).
 * Acquisition (searches, offers, candidates) lives in the sibling cards: every document here arrived by
 * confirming a candidate, including ones an admin supplied by offering a file.
 */
export const DocumentsCard: FC<DocumentsCardProps> = ({
    entries,
    candidates,
    saving,
    onEditPublication,
    onUnpublish,
    onSupersede,
    coverageContext,
    onAnalyzeLanguages,
    onApplyTrim,
    onRestoreTrim,
}) => {
    const docs = libraryDocuments(entries);
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Documents</CardTitle>
                <CardDescription>
                    Confirmed product documents. Those with app tags are live in the app for every home with this model;
                    the rest stay library-only.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {docs.length === 0 ? (
                    <div className="section-description">
                        No documents yet. Confirm a candidate below — offer a URL or a file under Acquisition if you
                        already have the document.
                    </div>
                ) : (
                    docs.map((entry) => (
                        <DocumentRow
                            key={entry.doc.id}
                            entry={entry}
                            entries={docs}
                            fit={documentLinkFit(entry.doc, coverageContext, families)}
                            candidate={candidates.find((candidate) => candidate.id === entry.doc.id)}
                            saving={saving}
                            onEditPublication={onEditPublication}
                            onUnpublish={onUnpublish}
                            onSupersede={onSupersede}
                            coverageContext={coverageContext}
                            onAnalyzeLanguages={onAnalyzeLanguages}
                            onApplyTrim={onApplyTrim}
                            onRestoreTrim={onRestoreTrim}
                        />
                    ))
                )}
            </CardContent>
        </Card>
    );
};

/** Why the system superseded a link — and so why it isn't cleared here: it would only be set again. */
const SUPERSEDED_VIA_NOTES: Record<SupersededVia, string> = {
    replaced: 'a newer revision; undo it on the document page',
    rule: 'the rule ranking: a direct link or a more specific rule wins',
};

const DocumentRow: FC<{
    entry: ModelDocumentEntry;
    /** Every library entry on the model — successor options and the superseded-by lookup. */
    entries: ModelDocumentEntry[];
    /** How the document's brands and types fit this model. */
    fit: DocumentLinkFit;
    /**
     * The doc's backing candidate (same content hash) — carries the language analysis. Absent on
     * migrated and pre-offer uploaded docs, which never had one.
     */
    candidate: ModelDocumentCandidate | undefined;
    saving: boolean;
    onEditPublication: DocumentsCardProps['onEditPublication'];
    onUnpublish: DocumentsCardProps['onUnpublish'];
    onSupersede: DocumentsCardProps['onSupersede'];
    coverageContext: DocumentsCardProps['coverageContext'];
    onAnalyzeLanguages: DocumentsCardProps['onAnalyzeLanguages'];
    onApplyTrim: DocumentsCardProps['onApplyTrim'];
    onRestoreTrim: DocumentsCardProps['onRestoreTrim'];
}> = ({
    entry,
    entries,
    fit,
    candidate,
    saving,
    onEditPublication,
    onUnpublish,
    onSupersede,
    coverageContext,
    onAnalyzeLanguages,
    onApplyTrim,
    onRestoreTrim,
}) => {
    const [action, setAction] = useState<'edit' | 'remove' | 'supersede' | 'trim' | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const { doc, link } = entry;
    const isSuperseded = isSupersededEntry(entry);
    // Only CURRENT docs are offered as successors — which is what makes supersession cycles unreachable —
    // and only those carrying this one's lead kind.
    const successorOptions = entries.filter(
        (e) => e.doc.id !== doc.id && !isSupersededEntry(e) && canSupersede(e.doc, doc),
    );
    // The pointer can dangle if the successor was later unpublished; the mark still stands (clearable).
    const successor = link.supersededBy ? entries.find((e) => e.doc.id === link.supersededBy)?.doc : undefined;
    const analysis = candidate?.languageAnalysis;
    const canAnalyze = canAnalyzeLanguages(candidate);
    // The cut reads the DOCUMENT's own stored copy, so it works with no candidate behind it (migrated
    // records, earlier cuts) — only the language route needs a candidate to analyze. It does need a
    // derivable storage path, though: that's what `trim.sourcePath` pins so the cut can be restored, and
    // offering the menu item without one would just fail server-side.
    const canTrim = !isSuperseded && !!documentStoragePath(doc);
    // What the PDF reads as NOW: fewer pages than the analysis counted once it's been cut.
    const pageCount = doc.trim ? countPagesInRanges(doc.trim.keepRanges) : analysis?.totalPages;

    return (
        <div className={cn('space-y-2 rounded-md border p-3', isSuperseded && 'opacity-60')}>
            <div className="flex flex-wrap items-center gap-2">
                <RouterLink
                    to={`/products/documents/${encodeURIComponent(doc.id)}`}
                    className="text-sm font-semibold underline-offset-4 hover:underline"
                >
                    {documentTitle(doc)}
                </RouterLink>
                <Badge variant="success">Published</Badge>
                <LinkKindBadge direct={isDirectLink(link)} />
                <LinkFitBadge fit={fit} />
                {isSuperseded && (
                    <Badge variant="secondary" className="border-transparent bg-orange-100 text-orange-900">
                        Superseded
                    </Badge>
                )}
                <DocumentStateBadges doc={doc} />
                {!doc.trim && analysis && (
                    <LanguageAnalysisBadge
                        analysis={analysis}
                        saving={saving}
                        onReanalyze={canAnalyze && canTrim ? () => onAnalyzeLanguages(doc.id) : undefined}
                    />
                )}
                <div className="ml-auto flex items-center gap-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setPreviewOpen((v) => !v)}>
                        {previewOpen ? 'Hide preview' : 'Preview'}
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Document actions">
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setAction('edit')}>
                                <Pencil className="h-4 w-4" />
                                Edit publication…
                            </DropdownMenuItem>
                            {canTrim && (
                                <DropdownMenuItem onSelect={() => setAction('trim')}>
                                    <Scissors className="h-4 w-4" />
                                    Trim PDF…
                                </DropdownMenuItem>
                            )}
                            {doc.trim && (
                                <DropdownMenuItem
                                    disabled={saving}
                                    onSelect={() => void onRestoreTrim('document', doc.id)}
                                >
                                    <RotateCcw className="h-4 w-4" />
                                    Restore full PDF
                                </DropdownMenuItem>
                            )}
                            {!isSuperseded && successorOptions.length > 0 && (
                                <DropdownMenuItem onSelect={() => setAction('supersede')}>
                                    <Replace className="h-4 w-4" />
                                    Mark superseded…
                                </DropdownMenuItem>
                            )}
                            {isSuperseded && isHumanMark(link) && (
                                <DropdownMenuItem disabled={saving} onSelect={() => void onSupersede(doc, null)}>
                                    <Undo2 className="h-4 w-4" />
                                    Clear superseded
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onSelect={() => setAction('remove')}>
                                <Trash2 className="h-4 w-4" />
                                Unpublish…
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <TagChips tags={doc.tags} />
            </div>
            <DocumentScopePills doc={doc} />
            {doc.coverage && <CoveragePills coverage={doc.coverage} />}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>Confirmed {new Date(link.linkedAt).toLocaleString()}</span>
                {isSuperseded && (
                    <span className="text-orange-700">
                        Superseded by {successor ? documentTitle(successor) : 'a removed document'}
                        {link.supersededVia && ` (${SUPERSEDED_VIA_NOTES[link.supersededVia]})`}
                    </span>
                )}
                <DocumentSourceLinks doc={doc} />
            </div>
            {previewOpen && <PdfPreview url={doc.storageUrl} title={documentTitle(doc)} />}

            <EditDocumentDialog
                open={action === 'edit'}
                onOpenChange={(open) => !open && setAction(null)}
                doc={doc}
                candidate={candidate}
                spaceId={coverageContext.spaceId}
                saving={saving}
                onSave={onEditPublication}
            />
            <UnpublishDialog
                open={action === 'remove'}
                onOpenChange={(open) => !open && setAction(null)}
                doc={doc}
                saving={saving}
                onUnpublish={onUnpublish}
            />
            <SupersedeDialog
                open={action === 'supersede'}
                onOpenChange={(open) => !open && setAction(null)}
                doc={doc}
                successorOptions={successorOptions}
                saving={saving}
                onSupersede={onSupersede}
            />
            <TrimPdfDialog
                open={action === 'trim'}
                onOpenChange={(open) => !open && setAction(null)}
                title={documentTitle(doc)}
                analysis={analysis}
                canAnalyze={canAnalyze}
                pageCount={pageCount}
                trimmed={!!doc.trim}
                saving={saving}
                outcome={
                    <>
                        These pages replace the document’s PDF. Everything else stays exactly as it is — its tags,
                        coverage rule, and every model linking it — so the cut is live wherever this document is served.
                        The full PDF is kept — “Restore full PDF” brings all of it back.
                    </>
                }
                submitLabel="Cut pages"
                onAnalyze={() => onAnalyzeLanguages(doc.id)}
                onApply={(cut) => onApplyTrim('document', doc.id, cut)}
            />
        </div>
    );
};

/** Pick which confirmed document replaces this one — only current (non-superseded) docs are offered. */
const SupersedeDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    doc: ModelDocument;
    successorOptions: ModelDocumentEntry[];
    saving: boolean;
    onSupersede: DocumentsCardProps['onSupersede'];
}> = ({ open, onOpenChange, doc, successorOptions, saving, onSupersede }) => {
    const [successorId, setSuccessorId] = useState<ModelDocumentId | undefined>(undefined);

    // Reset each open; with a single candidate successor (the common case), preselect it.
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset only on open (fresh array identity per render)
    useEffect(() => {
        if (open) setSuccessorId(successorOptions.length === 1 ? successorOptions[0]?.doc.id : undefined);
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Mark “{documentTitle(doc)}” superseded?</DialogTitle>
                    <DialogDescription>
                        For a document that a newer revision replaced — correct, just outdated. It stays in the library
                        with its replacement on record, but no user-facing surface serves it anymore. (For a wrong
                        document, unpublish it instead.)
                    </DialogDescription>
                </DialogHeader>
                <div className="min-w-0 space-y-2">
                    <Label htmlFor="supersede-successor">Replaced by</Label>
                    <Select
                        value={successorId}
                        onValueChange={(value) => setSuccessorId(value as ModelDocumentId)}
                        disabled={saving}
                    >
                        <SelectTrigger id="supersede-successor" className="w-full">
                            <SelectValue placeholder="Pick the current document" />
                        </SelectTrigger>
                        <SelectContent>
                            {successorOptions.map((option) => (
                                <SelectItem key={option.doc.id} value={option.doc.id}>
                                    {documentTitle(option.doc)} — confirmed{' '}
                                    {new Date(option.link.linkedAt).toLocaleDateString()}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={saving || !successorId}
                        onClick={async () => {
                            if (successorId && (await onSupersede(doc, successorId))) onOpenChange(false);
                        }}
                    >
                        Mark superseded
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const UnpublishDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    doc: ModelDocument;
    saving: boolean;
    onUnpublish: DocumentsCardProps['onUnpublish'];
}> = ({ open, onOpenChange, doc, saving, onUnpublish }) => (
    <AlertDialog open={open} onOpenChange={(next) => !next && onOpenChange(false)}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>Unpublish “{documentTitle(doc)}”?</AlertDialogTitle>
                <AlertDialogDescription>
                    This removes the document from the model’s library — and from the app, if it was shown there. Its
                    candidate returns to the review pool below — choose “not relevant” instead if the worker should
                    never re-propose it.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
                <AlertDialogAction disabled={saving} onClick={() => void onUnpublish(doc, false)}>
                    Unpublish
                </AlertDialogAction>
                <AlertDialogAction variant="destructive" disabled={saving} onClick={() => void onUnpublish(doc, true)}>
                    Unpublish + not relevant
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);
